// src/components/ui/primitives/button.tsx

/**
 * @fileoverview Button component
 * 
 * This component provides a button component for the shadcn/ui library.
 * 
 * @see https://ui.shadcn.com/docs/components/button
 * 
 * It is built on top of the shadcn/ui button component, with additional functionality and styles.
 * 
 */


import { forwardRef, type ComponentProps, type ElementType } from "react"
import { Slot, Slottable } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "~/lib/utils/design"

/**
 * Button variant styles using class-variance-authority
 * Defines the visual appearance and behavior variants for the button component
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      effect: {
        expandIcon: 'group gap-0 relative',
        ringHover: 'transition-all duration-300 hover:ring-2 hover:ring-primary/90 hover:ring-offset-2',
        glowingRing: 'relative overflow-hidden ring-2 ring-primary/50 animate-glow',
        shine:
          'before:animate-shine relative overflow-hidden before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.5)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-no-repeat background-position_0s_ease',
        shineHover:
          'relative overflow-hidden before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.5)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-[position:200%_0,0_0] before:bg-no-repeat before:transition-[background-position_0s_ease] hover:before:bg-[position:-100%_0,0_0] before:duration-1000',
        gooeyRight:
          'relative z-0 overflow-hidden transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:translate-x-[150%] before:translate-y-[150%] before:scale-[2.5] before:rounded-[100%] before:bg-gradient-to-r from-white/40 before:transition-transform before:duration-1000  hover:before:translate-x-[0%] hover:before:translate-y-[0%]',
        gooeyLeft:
          'relative z-0 overflow-hidden transition-all duration-500 after:absolute after:inset-0 after:-z-10 after:translate-x-[-150%] after:translate-y-[150%] after:scale-[2.5] after:rounded-[100%] after:bg-gradient-to-l from-white/40 after:transition-transform after:duration-1000  hover:after:translate-x-[0%] hover:after:translate-y-[0%]',
        underline:
          'relative !no-underline after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-left after:scale-x-100 hover:after:origin-bottom-right hover:after:scale-x-0 after:transition-transform after:ease-in-out after:duration-300',
        hoverUnderline:
          'relative !no-underline after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-right after:scale-x-0 hover:after:origin-bottom-left hover:after:scale-x-100 after:transition-transform after:ease-in-out after:duration-300',
        gradientSlideShow:
          'bg-[size:400%] bg-[linear-gradient(-45deg,var(--gradient-lime),var(--gradient-ocean),var(--gradient-wine),var(--gradient-rust))] animate-gradient-flow',
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * Icon placement options for buttons with icons
 * @typedef {Object} IconPlacement
 * @property {string} left - The icon is placed to the left of the button text
 * @property {string} right - The icon is placed to the right of the button text
 * @type {IconPlacement}
 * 
 */
type IconPlacement = 'left' | 'right'

/**
 * Props for buttons that include an icon
 * @interface ButtonWithIconProps
 * @property {ElementType} icon - The icon component to render
 * @property {IconPlacement} iconPlacement - Where to place the icon relative to the button text
 */
interface ButtonWithIconProps {
  icon: ElementType
  iconPlacement: IconPlacement
}

/**
 * Props for buttons without icons
 * Ensures icon-related props are not provided when not using an icon
 * 
 * @interface ButtonWithoutIconProps
 * @property {never} icon - The icon to apply to the button
 * @property {never} iconPlacement - The placement to apply to the button
 */
interface ButtonWithoutIconProps {
  icon?: never
  iconPlacement?: never
}

/**
 * Base button props extending HTML button attributes and variant props
 * @interface ButtonProps
 * @extends ComponentProps<"button">
 * @extends VariantProps<typeof buttonVariants>
 * 
 * @property {boolean} asChild - Whether the button should render as a Slot component
 * @property {boolean} loading - Whether the button is in a loading state
 * @property {string} loadingText - Text to display when loading
 */
export interface ButtonProps
  extends ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  /** 
   * When true, the button will render as a Slot component from Radix UI,
   * allowing it to merge props with its child element
   */
  asChild?: boolean
  
  /**
   * When true, the button will show a loading state with a spinner
   */
  loading?: boolean
  
  /**
   * Text to display when the button is in a loading state
   * @default "Processing..."
   */
  loadingText?: string
}

/**
 * Union type for button icon props - either has icon props or doesn't
 * @typedef {Object} ButtonIconProps
 * @property {ButtonWithIconProps} ButtonWithIconProps - The props for buttons with icons
 * @property {ButtonWithoutIconProps} ButtonWithoutIconProps - The props for buttons without icons
 * @type {ButtonIconProps}
 * 
 */
export type ButtonIconProps = ButtonWithIconProps | ButtonWithoutIconProps

/**
 * Combined props type for the Button component
 * @typedef {Object} ButtonComponentProps
 * @property {ButtonProps} ButtonProps - The props for the button
 * @property {ButtonIconProps} ButtonIconProps - The props for the button icon
 * @type {ButtonComponentProps}
 * 
 */
type ButtonComponentProps = ButtonProps & ButtonIconProps

/**
 * Button component with support for variants, effects, sizes, loading states, and optional icons
 * 
 * @param {ButtonComponentProps} props - The props for the button
 * @param {string} className - The className to apply to the button
 * @param {string} variant - The variant to apply to the button
 * @param {string} effect - The effect to apply to the button
 * @param {string} size - The size to apply to the button
 * @param {ElementType} icon - The icon to apply to the button
 * @param {IconPlacement} iconPlacement - The placement to apply to the button
 * @param {boolean} asChild - Whether the button should render as a Slot component
 * @param {boolean} loading - Whether the button is in a loading state
 * @param {string} loadingText - Text to display when loading
 * @param {ReactNode} children - The children to apply to the button
 * @param {boolean} disabled - Whether the button is disabled
 * @param {ComponentProps<"button">} props - The props to apply to the button
 * @param {RefObject<HTMLButtonElement>} ref - The ref to apply to the button
 * @returns The button component
 * 
 * @example
 * ```tsx
 * // Basic button
 * <Button>Click me</Button>
 * 
 * // Button with icon
 * <Button icon={ChevronRight} iconPlacement="right" effect="expandIcon">
 *   Next
 * </Button>
 * 
 * // Loading button
 * <Button loading loadingText="Saving...">
 *   Save Changes
 * </Button>
 * 
 * // As child (renders as the child element)
 * <Button asChild>
 *   <Link href="/about">About</Link>
 * </Button>
 * ```
 */
const Button = forwardRef<HTMLButtonElement, ButtonComponentProps>(
  (
    { 
      className, 
      variant, 
      effect, 
      size, 
      icon: Icon, 
      iconPlacement, 
      asChild = false,
      loading = false,
      loadingText = "Processing...",
      children, 
      disabled,
      ...props 
    }, 
    ref
  ): JSX.Element => {
    const Comp = asChild ? Slot : "button"

    /**
     * Renders an icon at the specified placement
     * @param placement - The placement to check against iconPlacement
     * @returns The icon element or null if conditions aren't met
     */
    const renderIcon = (placement: IconPlacement): JSX.Element | null => {
      if (!Icon || iconPlacement !== placement) {
        return null
      }

      if (effect === 'expandIcon') {
        const isLeft = placement === 'left'
        const expandClasses = isLeft
          ? "w-0 translate-x-[0%] pr-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-100 group-hover:pr-2 group-hover:opacity-100"
          : "w-0 translate-x-[100%] pl-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-0 group-hover:pl-2 group-hover:opacity-100"

        return (
          <div className={expandClasses}>
            <Icon />
          </div>
        )
      }

      return <Icon />
    }

    /**
     * Renders the button content, handling loading state
     * @returns The content to display inside the button
     */
    const renderContent = (): JSX.Element => {
      if (loading) {
        if (size === 'icon') {
          return <Loader2 className="animate-spin" />
        }
        
        return (
          <>
            <Slottable>{loadingText}</Slottable>
            <Loader2 className="ml-2 size-4 animate-spin" />
          </>
        )
      }

      return (
        <>
          {renderIcon('left')}
          <Slottable>{children}</Slottable>
          {renderIcon('right')}
        </>
      )
    }

    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, effect, size, className }))}
        ref={ref}
        disabled={loading || disabled}
        {...props}
      >
        {renderContent()}
      </Comp>
    )
  }
)

Button.displayName = 'Button'

export { Button, buttonVariants }
